import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../components/Common/Breadcrumb'
import HelpModal from '../../components/HelpModal/HelpModal'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import '../Accounts/PaymentForm.css'
import projectService from '../../services/projectService'
import paymentTermsMasterService from '../../services/paymentTermsMasterService'
import employeeMasterService from '../../services/employeeMasterService'
import { 
  Save, 
  X, 
  Plus, 
  Trash2, 
  Upload, 
  Eye,
  Calendar,
  DollarSign,
  Users,
  Settings,
  FileText,
  AlertCircle,
  RotateCcw,
  FlaskConical,
  Clock,
  Receipt,
  Bell,
  CheckCircle2,
  HelpCircle
} from 'lucide-react'

const isProjectDocument = (file) => {
  const name = String(file?.name || '').toLowerCase()
  return name.endsWith('.pdf') || name.endsWith('.doc') || name.endsWith('.docx')
}

const DocumentDropzone = ({ label, hint, file, disabled, onFile }) => {
  const inputId = useRef(`project-doc-${label.replace(/\W+/g, '-').toLowerCase()}`)
  const [dragOver, setDragOver] = useState(false)
  const savedName = file?.fileName || file?.filePath || ''

  const takeFile = (incoming) => {
    if (!incoming || disabled) return
    if (!isProjectDocument(incoming)) {
      toast.error('Upload a PDF, DOC, or DOCX file.')
      return
    }
    onFile(incoming)
  }

  return (
    <div className="form-group">
      <label className="form-label" htmlFor={inputId.current}>{label}</label>
      <div className="file-upload-area">
        <input
          id={inputId.current}
          type="file"
          accept=".pdf,.doc,.docx"
          disabled={disabled}
          className="file-input-hidden"
          style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0, overflow: 'hidden' }}
          onChange={(e) => {
            takeFile(e.target.files?.[0])
            e.target.value = ''
          }}
        />
        <label
          htmlFor={inputId.current}
          className="file-upload-label"
          style={{
            minHeight: 156,
            width: '100%',
            border: dragOver ? '2px dashed #6A4CA2' : '2px dashed #b7a8d6',
            borderRadius: 12,
            background: dragOver ? '#f3eefb' : '#faf9fc',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1
          }}
          onDragEnter={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (!disabled) setDragOver(true)
          }}
          onDragOver={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (!disabled) setDragOver(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setDragOver(false)
          }}
          onDrop={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setDragOver(false)
            takeFile(e.dataTransfer.files?.[0])
          }}
        >
          <Upload size={22} />
          <span style={{ fontWeight: 600, color: '#222448' }}>
            {dragOver ? 'Drop the file to upload' : hint}
          </span>
          <span className="file-upload-hint">or click to choose a PDF, DOC, or DOCX</span>
        </label>
      </div>
      {savedName && (
        <div className="pay-drop-file">
          <span>{file.fileName || savedName}</span>
          <button type="button" className="pay-drop-view" title="View file">
            <Eye size={16} />
          </button>
        </div>
      )}
    </div>
  )
}

const ProjectForm = ({ mode = 'create', projectId = null, initialData = null, breadcrumbItems: customBreadcrumbItems = null }) => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [projectDataLoading, setProjectDataLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, index: null, milestoneName: '' })
  const [masterDataLoaded, setMasterDataLoaded] = useState(false)
  const isEditMode = mode === 'edit'
  const isViewMode = mode === 'view'
  const isReadOnly = isViewMode
  const [masterData, setMasterData] = useState({
    projectTypes: [],
    projectCategories: [],
    priorities: [],
    customers: [],
    employees: [],
    projectTemplates: [],
    technologyStacks: [],
    resourceRoles: [],
    paymentTerms: []
  })

  const [formData, setFormData] = useState({
    // Basic Information
    projectNumber: '',
    projectName: '',
    projectDescription: '',
    projectType: '',
    projectCategory: '',
    projectStatus: '',
    projectPriority: '',
    projectTemplate: '',
    customerCategory: 'SME',
    customer: '',
    customerContact: '',
    currency: 'INR',
    remarks: '',
    tags: [],

    // Financial Information
    sowAmount: 0,
    outstandingAmount: 0,
    totalPaid: 0,
    billingModel: 'Fixed Price',
    billingFrequency: 'Monthly',
    paymentTerms: '',

    // Timeline Information
    plannedStartDate: '',
    plannedEndDate: '',
    actualStartDate: '',
    actualEndDate: '',
    estimatedDuration: 30,

    // Project Team
    projectManager: '',
    teamMembers: [],

    // Technology & Resources
    technologyStack: [],
    resourceRoles: [],

    // Milestones
    milestones: [],

    // Financial Milestones
    financialMilestones: [],

    // Cost Allocation
    costAllocation: {
      labor: 0,
      materials: 0,
      equipment: 0,
      travel: 0,
      overhead: 0,
      other: 0
    },

    // Documents
    documents: {
      sow: null,
      nda: null,
      po: null,
      contract: null,
      other: []
    },

    // Settings
    settings: {
      allowTimeTracking: true,
      allowExpenseTracking: true,
      requireApprovalForExpenses: true,
      autoInvoiceGeneration: false,
      notifyOnMilestoneCompletion: true,
      notifyOnBudgetExceeded: true,
      budgetThreshold: 90
    },

    // Risks
    risks: [],

    // Quality Metrics
    qualityMetrics: {
      codeQuality: 0,
      testCoverage: 0,
      defectDensity: 0,
      customerSatisfaction: 0
    },

    // Communication Plan
    communicationPlan: {
      stakeholderUpdates: {
        frequency: 'Weekly',
        format: 'Email'
      },
      teamMeetings: {
        frequency: 'Weekly',
        duration: 60
      }
    }
  })

  const [errors, setErrors] = useState({})
  const [newTag, setNewTag] = useState('')
  const [newMilestone, setNewMilestone] = useState({
    milestoneName: '',
    milestoneDescription: '',
    plannedDate: '',
    deliverables: [],
    acceptanceCriteria: [],
    isBillable: true,
    billingAmount: 0
  })
  const [newFinancialMilestone, setNewFinancialMilestone] = useState({
    milestoneName: '',
    description: '',
    plannedDate: '',
    amount: 0,
    paymentTerms: 'Net 30'
  })
  const [newRisk, setNewRisk] = useState({
    riskDescription: '',
    riskCategory: 'Technical',
    probability: 'Medium',
    impact: 'Medium',
    mitigationPlan: '',
    owner: ''
  })
  const [newTeamMember, setNewTeamMember] = useState({
    user: '',
    role: '',
    allocation: 100,
    startDate: new Date().toISOString().split('T')[0],
    isActive: true
  })

  // Load master data - only once
  useEffect(() => {
    if (!masterDataLoaded && !loading) {
      loadMasterData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load project data for edit/view mode - wait for master data to be loaded first
  useEffect(() => {
    if ((isEditMode || isViewMode) && projectId && masterDataLoaded) {
      if (initialData) {
        // If initialData is provided, use it directly
        console.log('Using initialData:', initialData)
        populateFormData(initialData)
      } else if (!projectDataLoading) {
        // Otherwise, load from API (only if not already loading)
        console.log('Loading project data for ID:', projectId)
        loadProjectData()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isEditMode, isViewMode, masterDataLoaded])

  const loadProjectData = async () => {
    try {
      setProjectDataLoading(true)
      const response = await projectService.getProjectById(projectId)
      
      if (response.success && response.data) {
        console.log('Project data loaded:', response.data)
        populateFormData(response.data)
      } else {
        toast.error(response.message || 'Failed to load project')
        navigate('/projects/list')
      }
    } catch (error) {
      console.error('Error loading project:', error)
      toast.error('Failed to load project')
      navigate('/projects/list')
    } finally {
      setProjectDataLoading(false)
    }
  }

  const populateFormData = (data) => {
    console.log('Populating form data:', data)
    console.log('Project Type from API:', data.projectType)
    console.log('Project Type _id:', data.projectType?._id)
    console.log('Project Type id:', data.projectType?.id)
    console.log('Master Data Project Types loaded:', masterData.projectTypes.length > 0)
    
    // Extract projectType ID - handle both populated object and direct ID
    // Backend may return _id or id field depending on JSON serialization
    let projectTypeValue = ''
    if (data.projectType) {
      if (typeof data.projectType === 'object' && data.projectType !== null) {
        // Try _id first (Mongoose default), then id (JSON serialized)
        // Check explicitly to avoid using the whole object as fallback
        if (data.projectType._id !== undefined && data.projectType._id !== null) {
          projectTypeValue = String(data.projectType._id)
        } else if (data.projectType.id !== undefined && data.projectType.id !== null) {
          projectTypeValue = String(data.projectType.id)
        } else {
          // If neither _id nor id exists, log warning
          console.warn('ProjectType object has no _id or id field:', data.projectType)
          projectTypeValue = ''
        }
      } else {
        // Direct ID value (string or number)
        projectTypeValue = String(data.projectType)
      }
    }
    console.log('Extracted projectType value:', projectTypeValue)
    console.log('Available projectType option IDs:', masterData.projectTypes.map(t => String(t._id || t.id)))
    
    setFormData({
      // Basic Information
      projectNumber: data.projectNumber || '',
      projectName: data.projectName || '',
      projectDescription: data.projectDescription || '',
      projectType: projectTypeValue,
      projectCategory: data.projectCategory?._id || data.projectCategory || '',
      projectStatus: typeof data.projectStatus === 'string' ? data.projectStatus : (data.projectStatus?._id || data.projectStatus || ''),
      projectPriority: data.projectPriority?._id || data.projectPriority || '',
      projectTemplate: data.projectTemplate?._id || data.projectTemplate || '',
      customerCategory: data.customerCategory || 'SME',
      customer: data.customer?._id || data.customer || '',
      customerContact: data.customerContact?._id || data.customerContact || '',
      currency: data.currency || 'USD',
      remarks: data.remarks || '',
      tags: data.tags || [],

      // Financial Information
      sowAmount: data.sowAmount || 0,
      outstandingAmount: data.outstandingAmount || 0,
      totalPaid: data.totalPaid || 0,
      billingModel: data.billingModel || 'Fixed Price',
      billingFrequency: data.billingFrequency || 'Monthly',
      paymentTerms: data.paymentTerms?._id || data.paymentTerms || '',

      // Timeline Information
      plannedStartDate: data.plannedStartDate ? new Date(data.plannedStartDate).toISOString().split('T')[0] : '',
      plannedEndDate: data.plannedEndDate ? new Date(data.plannedEndDate).toISOString().split('T')[0] : '',
      actualStartDate: data.actualStartDate ? new Date(data.actualStartDate).toISOString().split('T')[0] : '',
      actualEndDate: data.actualEndDate ? new Date(data.actualEndDate).toISOString().split('T')[0] : '',
      estimatedDuration: data.estimatedDuration || 0,

      // Project Team
      projectManager: data.projectManager?._id || data.projectManager || '',
      teamMembers: (data.teamMembers || []).map(member => ({
        user: member.user?._id || member.user || '',
        role: member.role || '',
        allocation: member.allocation || 100,
        startDate: member.startDate ? new Date(member.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        isActive: member.isActive !== undefined ? member.isActive : true
      })),

      // Technology & Resources
      technologyStack: (data.technologyStack || []).map(tech => tech._id || tech),
      resourceRoles: (data.resourceRoles || []).map(role => role._id || role),

      // Milestones
      milestones: (data.milestones || []).map(milestone => ({
        ...(milestone._id ? { _id: milestone._id } : {}),
        milestoneName: milestone.milestoneName || '',
        milestoneDescription: milestone.milestoneDescription || '',
        plannedDate: milestone.plannedDate ? new Date(milestone.plannedDate).toISOString().split('T')[0] : '',
        ...(milestone.actualDate ? { actualDate: new Date(milestone.actualDate).toISOString().split('T')[0] } : {}),
        status: milestone.status || 'Not Started',
        deliverables: milestone.deliverables || [],
        acceptanceCriteria: milestone.acceptanceCriteria || [],
        isBillable: milestone.isBillable !== undefined ? milestone.isBillable : true,
        billingAmount: milestone.billingAmount || 0
      })),

      // Financial Milestones
      financialMilestones: (data.financialMilestones || []).map(fm => ({
        milestoneName: fm.milestoneName || '',
        description: fm.description || '',
        plannedDate: fm.plannedDate ? new Date(fm.plannedDate).toISOString().split('T')[0] : '',
        amount: fm.amount || 0,
        paymentTerms: fm.paymentTerms || 'Net 30'
      })),

      // Cost Allocation
      costAllocation: data.costAllocation || {
        labor: 0,
        materials: 0,
        equipment: 0,
        travel: 0,
        overhead: 0,
        other: 0
      },

      // Documents
      documents: data.documents || {
        sow: null,
        nda: null,
        po: null,
        contract: null,
        other: []
      },

      // Settings
      settings: data.settings || {
        allowTimeTracking: true,
        allowExpenseTracking: true,
        requireApprovalForExpenses: true,
        autoInvoiceGeneration: false,
        notifyOnMilestoneCompletion: true,
        notifyOnBudgetExceeded: true,
        budgetThreshold: 90
      },
      risks: data.risks || [],
      qualityMetrics: data.qualityMetrics || {
        codeQuality: 0,
        testCoverage: 0,
        defectDensity: 0,
        customerSatisfaction: 0
      },
      communicationPlan: data.communicationPlan || {
        stakeholderUpdates: {
          frequency: 'Weekly',
          format: 'Email'
        },
        teamMeetings: {
          frequency: 'Weekly',
          duration: 60
        }
      },
      isActive: data.isActive || 'A'
    })
  }

  const loadMasterData = async () => {
    try {
      setLoading(true)
      const [
        projectTypesRes,
        projectCategoriesRes,
        prioritiesRes,
        customersRes,
        employeesRes,
        projectTemplatesRes,
        technologyStacksRes,
        resourceRolesRes,
        paymentTermsRes
      ] = await Promise.allSettled([
        projectService.getProjectTypes(),
        projectService.getProjectCategories(),
        projectService.getPriorities(),
        projectService.getCustomers(),
        employeeMasterService.getAllEmployees({ limit: 1000, status: 'Active' }),
        projectService.getProjectTemplates(),
        projectService.getTechnologyStacks(),
        projectService.getResourceRoles(),
        paymentTermsMasterService.getAllPaymentTerms({ limit: 1000, status: 'Active' })
      ])

      // Handle Promise.allSettled results
      const getData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          return result.value.data || result.value || []
        }
        return []
      }

      const getEmployeesData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
        }
        return []
      }

      setMasterData({
        projectTypes: getData(projectTypesRes),
        projectCategories: getData(projectCategoriesRes),
        priorities: getData(prioritiesRes),
        customers: getData(customersRes),
        employees: getEmployeesData(employeesRes),
        projectTemplates: getData(projectTemplatesRes),
        technologyStacks: getData(technologyStacksRes),
        resourceRoles: getData(resourceRolesRes),
        paymentTerms: getData(paymentTermsRes)
      })

      // Log any failed requests
      const failedRequests = [
        { name: 'Project Types', result: projectTypesRes },
        { name: 'Project Categories', result: projectCategoriesRes },
        { name: 'Priorities', result: prioritiesRes },
        { name: 'Customers', result: customersRes },
        { name: 'Employees', result: employeesRes },
        { name: 'Project Templates', result: projectTemplatesRes },
        { name: 'Technology Stacks', result: technologyStacksRes },
        { name: 'Resource Roles', result: resourceRolesRes },
        { name: 'Payment Terms', result: paymentTermsRes }
      ].filter(item => item.result.status === 'rejected')

      if (failedRequests.length > 0) {
        console.warn('Some master data failed to load:', failedRequests.map(r => r.name))
        // Check if errors were already handled by interceptor
        const hasNetworkError = failedRequests.some(r => {
          const error = r.result.reason
          return error && (error._errorHandled || error.request)
        })
        if (!hasNetworkError) {
          if (failedRequests.length === 9) {
            toast.error('Unable to connect to backend server. Please ensure the server is running.')
          } else {
            toast.warning(`Failed to load some data: ${failedRequests.map(r => r.name).join(', ')}`)
          }
        }
      } else {
        console.log('All master data loaded successfully')
      }
      
      setMasterDataLoaded(true)
    } catch (error) {
      console.error('Error loading master data:', error)
      if (!error._errorHandled && !error.request) {
        toast.error('Failed to load master data. Please check your connection.')
      }
      setMasterDataLoaded(true) // Set to true even on error to prevent infinite retries
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const handleNestedInputChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }))
  }

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }))
      setNewTag('')
    }
  }

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const addMilestone = () => {
    if (newMilestone.milestoneName.trim() && newMilestone.plannedDate) {
      // Validate date is within project timeline
      const milestoneDate = new Date(newMilestone.plannedDate)
      const projectStart = formData.plannedStartDate ? new Date(formData.plannedStartDate) : null
      const projectEnd = formData.plannedEndDate ? new Date(formData.plannedEndDate) : null
      
      if (projectStart && milestoneDate < projectStart) {
        toast.error('Milestone date cannot be before project start date')
        return
      }
      
      if (projectEnd && milestoneDate > projectEnd) {
        toast.error('Milestone date cannot be after project end date')
        return
      }
      
      // Check for duplicate milestone names
      const isDuplicate = formData.milestones.some(
        milestone => milestone.milestoneName.toLowerCase() === newMilestone.milestoneName.toLowerCase()
      )
      
      if (isDuplicate) {
        toast.error('A milestone with this name already exists')
        return
      }
      
      setFormData(prev => ({
        ...prev,
        milestones: [...prev.milestones, { ...newMilestone }]
      }))
      
      setNewMilestone({
        milestoneName: '',
        milestoneDescription: '',
        plannedDate: '',
        deliverables: [],
        acceptanceCriteria: [],
        isBillable: true,
        billingAmount: 0
      })
      
      toast.success('Milestone added successfully!')
    } else {
      toast.error('Please fill in the milestone name and planned date')
    }
  }

  const removeMilestone = (index) => {
    const milestone = formData.milestones[index]
    setDeleteModal({
      isOpen: true,
      index: index,
      milestoneName: milestone.milestoneName
    })
  }

  const handleConfirmDelete = () => {
    if (deleteModal.index !== null) {
      setFormData(prev => ({
        ...prev,
        milestones: prev.milestones.filter((_, i) => i !== deleteModal.index)
      }))
      toast.success('Milestone removed successfully!')
      setDeleteModal({ isOpen: false, index: null, milestoneName: '' })
    }
  }

  const updateMilestone = (index, field, value) => {
    if (field === 'plannedDate' && value) {
      const milestoneDate = new Date(value)
      const projectStart = formData.plannedStartDate ? new Date(formData.plannedStartDate) : null
      const projectEnd = formData.plannedEndDate ? new Date(formData.plannedEndDate) : null

      if (projectStart && milestoneDate < projectStart) {
        toast.error('Milestone date cannot be before project start date')
        return
      }
      if (projectEnd && milestoneDate > projectEnd) {
        toast.error('Milestone date cannot be after project end date')
        return
      }
    }

    setFormData(prev => ({
      ...prev,
      milestones: prev.milestones.map((milestone, milestoneIndex) => (
        milestoneIndex === index ? { ...milestone, [field]: value } : milestone
      ))
    }))
  }

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, index: null, milestoneName: '' })
  }

  const addFinancialMilestone = () => {
    if (newFinancialMilestone.milestoneName && newFinancialMilestone.plannedDate && newFinancialMilestone.amount > 0) {
      setFormData(prev => ({
        ...prev,
        financialMilestones: [...prev.financialMilestones, { ...newFinancialMilestone }]
      }))
      setNewFinancialMilestone({
        milestoneName: '',
        description: '',
        plannedDate: '',
        amount: 0,
        paymentTerms: 'Net 30'
      })
    }
  }

  const removeFinancialMilestone = (index) => {
    setFormData(prev => ({
      ...prev,
      financialMilestones: prev.financialMilestones.filter((_, i) => i !== index)
    }))
  }

  const addRisk = () => {
    if (newRisk.riskDescription) {
      setFormData(prev => ({
        ...prev,
        risks: [...prev.risks, { ...newRisk }]
      }))
      setNewRisk({
        riskDescription: '',
        riskCategory: 'Technical',
        probability: 'Medium',
        impact: 'Medium',
        mitigationPlan: '',
        owner: ''
      })
    }
  }

  const removeRisk = (index) => {
    setFormData(prev => ({
      ...prev,
      risks: prev.risks.filter((_, i) => i !== index)
    }))
  }

  const addTeamMember = () => {
    if (newTeamMember.user && newTeamMember.role) {
      setFormData(prev => ({
        ...prev,
        teamMembers: [...prev.teamMembers, { ...newTeamMember }]
      }))
      setNewTeamMember({
        user: '',
        role: '',
        allocation: 100,
        startDate: new Date().toISOString().split('T')[0],
        isActive: true
      })
    }
  }

  const removeTeamMember = (index) => {
    setFormData(prev => ({
      ...prev,
      teamMembers: prev.teamMembers.filter((_, i) => i !== index)
    }))
  }

  const handleFileUpload = async (file, type) => {
    try {
      const response = await projectService.uploadFile(file, type)
      if (response.success) {
        setFormData(prev => ({
          ...prev,
          documents: {
            ...prev.documents,
            [type]: {
              fileName: file.name,
              filePath: response.data.filePath,
              fileSize: file.size,
              uploadedAt: new Date()
            }
          }
        }))
        toast.success('File uploaded successfully')
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      toast.error(error.response?.data?.message || 'Failed to upload file')
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Required fields validation
    if (!formData.projectNumber.trim()) newErrors.projectNumber = 'Project number is required'
    if (!formData.projectName.trim()) newErrors.projectName = 'Project name is required'
    if (!formData.projectType) newErrors.projectType = 'Project type is required'
    if (!formData.projectStatus) newErrors.projectStatus = 'Status is required'
    if (!formData.customer) newErrors.customer = 'Customer is required'

    // Date validation (only when both dates are provided)
    if (formData.plannedStartDate && formData.plannedEndDate) {
      if (new Date(formData.plannedStartDate) >= new Date(formData.plannedEndDate)) {
        newErrors.plannedEndDate = 'Planned end date must be after planned start date'
      }
    }

    // Financial validation
    if (formData.sowAmount < 0) newErrors.sowAmount = 'SOW amount cannot be negative'
    if (formData.outstandingAmount < 0) newErrors.outstandingAmount = 'Outstanding amount cannot be negative'
    if (formData.totalPaid < 0) newErrors.totalPaid = 'Total paid cannot be negative'

    const milestoneNames = formData.milestones.map(milestone => (milestone.milestoneName || '').trim().toLowerCase())
    if (formData.milestones.some(milestone => !(milestone.milestoneName || '').trim() || !milestone.plannedDate)) {
      newErrors.milestones = 'Each milestone needs a name and planned date'
    } else if (milestoneNames.some((name, index) => name && milestoneNames.indexOf(name) !== index)) {
      newErrors.milestones = 'Milestone names must be unique'
    }

    setErrors(newErrors)
    
    // Log errors for debugging
    if (Object.keys(newErrors).length > 0) {
      console.log('Validation errors:', newErrors)
      // Scroll to first error field
      const firstErrorField = Object.keys(newErrors)[0]
      const errorElement = document.getElementById(firstErrorField) || document.querySelector(`[name="${firstErrorField}"]`)
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        errorElement.focus()
      }
    }
    
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      const errorCount = Object.keys(errors).length
      const errorFields = Object.keys(errors).map(field => {
        const fieldNames = {
          projectNumber: 'Project Number',
          projectName: 'Project Name',
          projectType: 'Project Type',
          projectStatus: 'Status',
          projectCategory: 'Project Category',
          customer: 'Customer',
          projectManager: 'Project Manager',
          plannedStartDate: 'Planned Start Date',
          plannedEndDate: 'Planned End Date',
          sowAmount: 'SOW Amount',
          outstandingAmount: 'Outstanding Amount',
          totalPaid: 'Total Paid',
          milestones: 'Milestones'
        }
        return fieldNames[field] || field
      }).join(', ')
      toast.error(`Please fix ${errorCount} error(s): ${errorFields}`)
      return
    }

    try {
      setLoading(true)
      let response
      if (isEditMode) {
        response = await projectService.updateProject(projectId, formData)
        if (response.success) {
          toast.success('Project updated successfully')
          navigate('/projects/list')
        } else {
          toast.error(response.message || 'Failed to update project')
        }
      } else {
        response = await projectService.createProject(formData)
        if (response.success) {
          toast.success('Project created successfully')
          navigate('/projects/list')
        } else {
          toast.error(response.message || 'Failed to create project')
        }
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} project:`, error)
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} project`)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      projectNumber: '',
      projectName: '',
      projectDescription: '',
      projectType: '',
      projectCategory: '',
      projectStatus: '',
      projectPriority: '',
      projectTemplate: '',
      customerCategory: 'SME',
      customer: '',
      customerContact: '',
      currency: 'INR',
      remarks: '',
      tags: [],
      sowAmount: 0,
      outstandingAmount: 0,
      totalPaid: 0,
      billingModel: 'Fixed Price',
      billingFrequency: 'Monthly',
      paymentTerms: '',
      plannedStartDate: '',
      plannedEndDate: '',
      actualStartDate: '',
      actualEndDate: '',
      estimatedDuration: 30,
      projectManager: '',
      teamMembers: [],
      technologyStack: [],
      resourceRoles: [],
      milestones: [],
      financialMilestones: [],
      costAllocation: {
        labor: 0,
        materials: 0,
        equipment: 0,
        travel: 0,
        overhead: 0,
        other: 0
      },
      documents: {
        sow: null,
        nda: null,
        po: null,
        contract: null,
        other: []
      },
      settings: {
        allowTimeTracking: true,
        allowExpenseTracking: true,
        requireApprovalForExpenses: true,
        autoInvoiceGeneration: false,
        notifyOnMilestoneCompletion: true,
        notifyOnBudgetExceeded: true,
        budgetThreshold: 90
      },
      risks: [],
      qualityMetrics: {
        codeQuality: 0,
        testCoverage: 0,
        defectDensity: 0,
        customerSatisfaction: 0
      },
      communicationPlan: {
        stakeholderUpdates: {
          frequency: 'Weekly',
          format: 'Email'
        },
        teamMeetings: {
          frequency: 'Weekly',
          duration: 60
        }
      }
    })
    setErrors({})
  }

  const handleTestData = () => {
    const today = new Date()
    const startDate = new Date(today)
    startDate.setDate(today.getDate() + 7) // 7 days from now
    const endDate = new Date(today)
    endDate.setDate(today.getDate() + 90) // 90 days from now

    setFormData({
      // Basic Information
      projectNumber: `PRJ-${Date.now().toString().slice(-6)}`,
      projectName: 'E-Commerce Platform Development',
      projectDescription: 'Development of a modern e-commerce platform with payment integration and inventory management',
      projectType: masterData.projectTypes.length > 0 ? masterData.projectTypes[0]._id : '',
      projectCategory: masterData.projectCategories.length > 0 ? masterData.projectCategories[0]._id : '',
      projectStatus: 'Active',
      projectPriority: masterData.priorities.length > 0 ? masterData.priorities[0]._id : '',
      projectTemplate: masterData.projectTemplates.length > 0 ? masterData.projectTemplates[0]._id : '',
      customerCategory: 'Enterprise',
      customer: masterData.customers.length > 0 ? masterData.customers[0]._id : '',
      customerContact: masterData.customers.length > 0 ? masterData.customers[0]._id : '',
      currency: 'USD',
      remarks: 'High priority project with aggressive timeline',
      tags: ['E-Commerce', 'Web Development', 'Payment Integration', 'High Priority'],

      // Financial Information
      sowAmount: 150000,
      outstandingAmount: 150000,
      totalPaid: 0,
      billingModel: 'Fixed Price',
      billingFrequency: 'Monthly',
      paymentTerms: masterData.paymentTerms.length > 0 ? masterData.paymentTerms[0]._id : '',

      // Timeline Information
      plannedStartDate: startDate.toISOString().split('T')[0],
      plannedEndDate: endDate.toISOString().split('T')[0],
      actualStartDate: '',
      actualEndDate: '',
      estimatedDuration: 90,

      // Project Team
      projectManager: masterData.employees.length > 0 ? masterData.employees[0]._id : '',
      teamMembers: masterData.employees.length > 1 ? [
        {
          user: masterData.employees[1]._id,
          role: masterData.resourceRoles.length > 0 ? masterData.resourceRoles[0]._id : '',
          allocation: 100,
          startDate: startDate.toISOString().split('T')[0],
          isActive: true
        }
      ] : [],

      // Technology & Resources
      technologyStack: masterData.technologyStacks.length > 0 ? [masterData.technologyStacks[0]._id] : [],
      resourceRoles: masterData.resourceRoles.length > 0 ? [masterData.resourceRoles[0]._id] : [],

      // Milestones
      milestones: [
        {
          milestoneName: 'Project Kickoff',
          milestoneDescription: 'Complete project setup, team onboarding, and initial planning',
          plannedDate: new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days after start
          deliverables: ['Project Plan', 'Team Roster', 'Communication Plan'],
          acceptanceCriteria: ['All team members onboarded', 'Project plan approved'],
          isBillable: true,
          billingAmount: 15000
        },
        {
          milestoneName: 'Design Phase Completion',
          milestoneDescription: 'Complete UI/UX design and get client approval',
          plannedDate: new Date(startDate.getTime() + 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 21 days after start
          deliverables: ['UI Mockups', 'Design System', 'Client Approval'],
          acceptanceCriteria: ['All designs approved by client', 'Design system documented'],
          isBillable: true,
          billingAmount: 30000
        },
        {
          milestoneName: 'Development Phase 1',
          milestoneDescription: 'Complete core functionality development',
          plannedDate: new Date(startDate.getTime() + 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 45 days after start
          deliverables: ['Core Features', 'API Integration', 'Database Setup'],
          acceptanceCriteria: ['All core features functional', 'API endpoints tested'],
          isBillable: true,
          billingAmount: 50000
        },
        {
          milestoneName: 'Testing & QA',
          milestoneDescription: 'Complete testing, bug fixes, and quality assurance',
          plannedDate: new Date(startDate.getTime() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 60 days after start
          deliverables: ['Test Reports', 'Bug Fixes', 'QA Sign-off'],
          acceptanceCriteria: ['All critical bugs fixed', 'Test coverage > 80%'],
          isBillable: false,
          billingAmount: 0
        },
        {
          milestoneName: 'Project Launch',
          milestoneDescription: 'Deploy to production and go live',
          plannedDate: new Date(startDate.getTime() + 75 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 75 days after start
          deliverables: ['Production Deployment', 'Launch Documentation', 'Training Materials'],
          acceptanceCriteria: ['System live in production', 'Client training completed'],
          isBillable: true,
          billingAmount: 55000
        }
      ],

      // Financial Milestones
      financialMilestones: [],

      // Cost Allocation
      costAllocation: {
        labor: 100000,
        materials: 20000,
        equipment: 15000,
        travel: 10000,
        overhead: 3000,
        other: 2000
      },

      // Documents
      documents: {
        sow: null,
        nda: null,
        po: null,
        contract: null,
        other: []
      },

      // Settings
      settings: {
        allowTimeTracking: true,
        allowExpenseTracking: true,
        requireApprovalForExpenses: true,
        autoInvoiceGeneration: false,
        notifyOnMilestoneCompletion: true,
        notifyOnBudgetExceeded: true,
        budgetThreshold: 90
      },
      risks: [],
      qualityMetrics: {
        codeQuality: 0,
        testCoverage: 0,
        defectDensity: 0,
        customerSatisfaction: 0
      },
      communicationPlan: {
        stakeholderUpdates: {
          frequency: 'Weekly',
          format: 'Email'
        },
        teamMeetings: {
          frequency: 'Weekly',
          duration: 60
        }
      }
    })
    setErrors({})
    toast.success('Test data loaded successfully!')
  }

  // Listen for F1 key to populate test data (only in create mode)
  useEffect(() => {
    if (isEditMode || isViewMode) return // Don't add listener in edit/view mode

    const handleKeyPress = (event) => {
      // Check if F1 key is pressed (keyCode 112 or key === 'F1')
      if (event.key === 'F1' || event.keyCode === 112) {
        event.preventDefault() // Prevent browser help menu
        handleTestData()
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleKeyPress)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [isEditMode, isViewMode, masterData]) // Include dependencies used by handleTestData

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Project List', path: '/projects/list', onClick: () => navigate('/projects/list') },
    { label: 'Create New', path: '/projects/new', isCurrent: true }
  ]

  const tabs = [
    { id: 'basic', label: 'Basic Information', icon: FileText },
    { id: 'financial', label: 'Financial Details', icon: DollarSign },
    { id: 'timeline', label: 'Timeline & Team', icon: Calendar },
    { id: 'milestones', label: 'Milestones', icon: Settings },
    { id: 'documents', label: 'Documents', icon: Upload },
    { id: 'settings', label: 'Settings', icon: Settings }
  ]

  const helpContent = (
    <>
      <h3>What is Project Entry?</h3>
      <p>Project Entry lets you <strong>create and manage project information</strong> for the Project List. You define basic details, financials, timeline, team, milestones, documents, and settings.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Information</strong> – Project name, code, type, category, status, priority, customer, start/end dates, description.</p>
      <p><strong>Financial Details</strong> – Budget, currency, payment terms, billing type, and financial milestones.</p>
      <p><strong>Timeline &amp; Team</strong> – Timeline settings and team members (role, allocation, dates).</p>
      <p><strong>Milestones</strong> – Project milestones (name, due date, status, amount if billable).</p>
      <p><strong>Documents</strong> – Attach and manage project documents.</p>
      <p><strong>Settings</strong> – Additional project settings and options.</p>
      <h3>How to use</h3>
      <p>Fill required fields in Basic Information. Use the tabs to complete financials, team, milestones, and documents. Use <strong>Save</strong> to create or update. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>{isViewMode ? 'View Project' : isEditMode ? 'Edit Project' : 'Project Entry'}</h1>
          <p>{isViewMode ? 'Project information' : isEditMode ? 'Update this project' : 'Create and manage project information'}</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container pay-entry">
        <div className="sac-form-header">
          <h2>Project Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                type="button"
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </div>

        <form className="form-content" onSubmit={handleSubmit}>
          {/* Validation Error Summary */}
          {Object.keys(errors).length > 0 && (
            <div style={{
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #dc2626',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <AlertCircle size={18} style={{ color: '#dc2626', flexShrink: 0 }} />
                <h4 style={{
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#991b1b'
                }}>
                  Please fix the following {Object.keys(errors).length} error(s):
                </h4>
              </div>
              <ul style={{
                margin: 0,
                paddingLeft: '24px',
                fontSize: '13px',
                color: '#991b1b',
                listStyle: 'disc'
              }}>
                {Object.entries(errors).map(([field, message]) => {
                  const fieldNames = {
                    projectNumber: 'Project Number',
                    projectName: 'Project Name',
                    projectType: 'Project Type',
                    projectStatus: 'Status',
                    projectCategory: 'Project Category',
                    customer: 'Customer',
                    projectManager: 'Project Manager',
                    plannedStartDate: 'Planned Start Date',
                    plannedEndDate: 'Planned End Date',
                    sowAmount: 'SOW Amount',
                    outstandingAmount: 'Outstanding Amount',
                    totalPaid: 'Total Paid'
                  }
                  return (
                    <li key={field} style={{ marginBottom: '4px', cursor: 'pointer' }}
                        onClick={() => {
                          const element = document.getElementById(field) || document.querySelector(`[name="${field}"]`)
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                            element.focus()
                          }
                        }}>
                      <strong>{fieldNames[field] || field}:</strong> {message}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
          
          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'basic' && (
              <>
                <section className="pay-block">
                  <div className="pay-block-head">
                    <div>
                      <h3>Project</h3>
                      <p>The number, name, and type of this project.</p>
                    </div>
                  </div>
                <div className="pay-grid pay-grid-3">
                  {/* Project Number */}
                  <div className="form-group">
                    <label htmlFor="projectNumber" className="form-label required">
                      Project Number
                    </label>
                    <input
                      id="projectNumber"
                      type="text"
                      value={formData.projectNumber}
                      onChange={(e) => handleInputChange('projectNumber', e.target.value)}
                      className="form-input"
                      placeholder="Enter project number"
                      readOnly={isEditMode || isReadOnly}
                      disabled={isEditMode || isReadOnly}
                      style={isEditMode || isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.projectNumber && (
                      <p className="form-error">{errors.projectNumber}</p>
                    )}
                  </div>

                  {/* Project Name */}
                  <div className="form-group">
                    <label htmlFor="projectName" className="form-label required">
                      Project Name
                    </label>
                    <input
                      id="projectName"
                      type="text"
                      value={formData.projectName}
                      onChange={(e) => handleInputChange('projectName', e.target.value)}
                      className="form-input"
                      placeholder="Enter project name"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.projectName && (
                      <p className="form-error">{errors.projectName}</p>
                    )}
                  </div>

                  {/* Project Type */}
                  <div className="form-group">
                    <label htmlFor="projectType" className="form-label required">
                      Project Type
                    </label>
                    <select
                      id="projectType"
                      value={formData.projectType}
                      onChange={(e) => handleInputChange('projectType', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Project Type</option>
                      {masterData.projectTypes.map((type) => {
                        // Handle both _id and id fields (Mongoose vs JSON serialized)
                        const typeId = type._id || type.id
                        return (
                          <option key={typeId} value={String(typeId)}>
                            {type.projectTypeName}
                          </option>
                        )
                      })}
                    </select>
                    {errors.projectType && (
                      <p className="form-error">{errors.projectType}</p>
                    )}
                  </div>
                </div>
                </section>

                <section className="pay-block">
                  <div className="pay-block-head">
                    <div>
                      <h3>Customer</h3>
                      <p>Who the project is for, and the currency it is billed in.</p>
                    </div>
                  </div>
                <div className="pay-grid pay-grid-3">
                  {/* Customer Category */}
                  <div className="form-group">
                    <label htmlFor="customerCategory" className="form-label required">
                      Customer Category
                    </label>
                    <select
                      id="customerCategory"
                      value={formData.customerCategory}
                      onChange={(e) => handleInputChange('customerCategory', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="Individual">Individual</option>
                      <option value="SME">SME</option>
                      <option value="Enterprise">Enterprise</option>
                      <option value="Government">Government</option>
                      <option value="Non-Profit">Non-Profit</option>
                    </select>
                  </div>

                  {/* Customer */}
                  <div className="form-group">
                    <label htmlFor="customer" className="form-label required">
                      Customer
                    </label>
                    <select
                      id="customer"
                      value={formData.customer}
                      onChange={(e) => handleInputChange('customer', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Customer</option>
                      {masterData.customers.map((customer) => (
                        <option key={customer._id} value={customer._id}>
                          {customer.customerName}
                        </option>
                      ))}
                    </select>
                    {errors.customer && (
                      <p className="form-error">{errors.customer}</p>
                    )}
                  </div>

                  {/* Currency */}
                  <div className="form-group">
                    <label htmlFor="currency" className="form-label required">
                      Currency
                    </label>
                    <select
                      id="currency"
                      value={formData.currency}
                      onChange={(e) => handleInputChange('currency', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="CAD">CAD</option>
                      <option value="AUD">AUD</option>
                    </select>
                  </div>
                </div>
                </section>

                <section className="pay-block">
                  <div className="pay-block-head">
                    <div>
                      <h3>Classification</h3>
                      <p>Status, a short description, and the category this project belongs to.</p>
                    </div>
                  </div>
                <div className="pay-grid pay-grid-3">
                  {/* Status */}
                  <div className="form-group">
                    <label htmlFor="projectStatus" className="form-label required">
                      Status
                    </label>
                    <select
                      id="projectStatus"
                      value={formData.projectStatus}
                      onChange={(e) => handleInputChange('projectStatus', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Status</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                    {errors.projectStatus && (
                      <p className="form-error">{errors.projectStatus}</p>
                    )}
                  </div>

                  {/* Project Description */}
                  <div className="form-group">
                    <label htmlFor="projectDescription" className="form-label">
                      Project Description
                    </label>
                    <input
                      id="projectDescription"
                      type="text"
                      value={formData.projectDescription}
                      onChange={(e) => handleInputChange('projectDescription', e.target.value)}
                      className="form-input"
                      placeholder="Enter project description"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Project Category */}
                  <div className="form-group">
                    <label htmlFor="projectCategory" className="form-label">
                      Project Category
                    </label>
                    <select
                      id="projectCategory"
                      value={formData.projectCategory}
                      onChange={(e) => handleInputChange('projectCategory', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Category</option>
                      {masterData.projectCategories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.categoryName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                </section>

                <section className="pay-block">
                  <div className="pay-block-head">
                    <div>
                      <h3>Tags</h3>
                      <p>Short labels that make this project easier to find.</p>
                    </div>
                  </div>
                <div className="form-group">
                  <label className="form-label">Tags</label>
                  {!isReadOnly && (
                    <div style={{ 
                      display: 'flex', 
                      gap: '8px', 
                      marginBottom: '12px',
                      alignItems: 'center'
                    }}>
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                        className="form-input"
                        placeholder="Enter tag and press Enter or click Add"
                        style={{ flex: 1, maxWidth: '400px' }}
                      />
                      <button
                        type="button"
                        onClick={addTag}
                        className="btn-secondary"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        Add
                      </button>
                    </div>
                  )}
                  {formData.tags.length > 0 && (
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '8px',
                      padding: '12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef',
                      minHeight: '48px'
                    }}>
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            backgroundColor: '#292752',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: '500',
                            fontFamily: 'Inter, sans-serif'
                          }}
                        >
                          {tag}
                          {!isReadOnly && (
                            <button
                              type="button"
                              onClick={() => removeTag(tag)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                              }}
                              title="Remove tag"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                </section>
              </>
            )}

            {activeTab === 'financial' && (
              <>
                <section className="pay-block">
                  <div className="pay-block-head">
                    <div>
                      <h3>Contract value</h3>
                      <p>The statement of work amount, what is still to collect, and what has been paid.</p>
                    </div>
                  </div>
                <div className="pay-grid pay-grid-3">
                  {/* SOW Amount */}
                  <div className="form-group">
                    <label htmlFor="sowAmount" className="form-label">
                      SOW Amount
                    </label>
                    <input
                      id="sowAmount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.sowAmount}
                      onChange={(e) => handleInputChange('sowAmount', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      placeholder="0.00"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.sowAmount && (
                      <p className="form-error">{errors.sowAmount}</p>
                    )}
                  </div>

                  {/* Outstanding Amount */}
                  <div className="form-group">
                    <label htmlFor="outstandingAmount" className="form-label">
                      Outstanding Amount
                    </label>
                    <input
                      id="outstandingAmount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.outstandingAmount}
                      onChange={(e) => handleInputChange('outstandingAmount', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      placeholder="0.00"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.outstandingAmount && (
                      <p className="form-error">{errors.outstandingAmount}</p>
                    )}
                  </div>

                  {/* Total Paid */}
                  <div className="form-group">
                    <label htmlFor="totalPaid" className="form-label">
                      Total Paid
                    </label>
                    <input
                      id="totalPaid"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.totalPaid}
                      onChange={(e) => handleInputChange('totalPaid', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      placeholder="0.00"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.totalPaid && (
                      <p className="form-error">{errors.totalPaid}</p>
                    )}
                  </div>
                </div>
                </section>

                <section className="pay-block">
                  <div className="pay-block-head">
                    <div>
                      <h3>Billing</h3>
                      <p>How this project is billed, and the payment terms.</p>
                    </div>
                  </div>
                <div className="pay-grid pay-grid-3">
                  {/* Billing Model */}
                  <div className="form-group">
                    <label htmlFor="billingModel" className="form-label">
                      Billing Model
                    </label>
                    <select
                      id="billingModel"
                      value={formData.billingModel}
                      onChange={(e) => handleInputChange('billingModel', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="Fixed Price">Fixed Price</option>
                      <option value="Time & Material">Time & Material</option>
                      <option value="Staff Augmentation">Staff Augmentation</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="Milestone-based">Milestone-based</option>
                    </select>
                  </div>

                  {/* Billing Frequency */}
                  <div className="form-group">
                    <label htmlFor="billingFrequency" className="form-label">
                      Billing Frequency
                    </label>
                    <select
                      id="billingFrequency"
                      value={formData.billingFrequency}
                      onChange={(e) => handleInputChange('billingFrequency', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="Weekly">Weekly</option>
                      <option value="Bi-weekly">Bi-weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Milestone-based">Milestone-based</option>
                      <option value="Project-based">Project-based</option>
                    </select>
                  </div>

                  {/* Payment Terms */}
                  <div className="form-group">
                    <label htmlFor="paymentTerms" className="form-label">
                      Payment Terms
                    </label>
                    <select
                      id="paymentTerms"
                      value={formData.paymentTerms}
                      onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Payment Terms</option>
                      {masterData.paymentTerms.map((term) => (
                        <option key={term._id} value={term._id}>
                          {term.paymentTermsCode} - {term.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                </section>

                <section className="pay-block">
                  <div className="pay-block-head">
                    <div>
                      <h3>Cost allocation</h3>
                      <p>How the project cost is split.</p>
                    </div>
                  </div>
                <div className="pay-grid pay-grid-3">
                    {Object.entries(formData.costAllocation).map(([key, value]) => (
                      <div key={key} className="form-group">
                        <label htmlFor={`costAllocation_${key}`} className="form-label capitalize">
                          {key}
                        </label>
                        <input
                          id={`costAllocation_${key}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={value}
                          onChange={(e) => handleNestedInputChange('costAllocation', key, parseFloat(e.target.value) || 0)}
                          className="form-input"
                          placeholder="0.00"
                          readOnly={isReadOnly}
                          disabled={isReadOnly}
                          style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              </>
            )}

            {activeTab === 'timeline' && (
              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Schedule</h3>
                    <p>When the project runs, and who manages it.</p>
                  </div>
                </div>
                <div className="pay-grid pay-grid-3">
                  {/* Planned Start Date */}
                  <div className="form-group">
                    <label htmlFor="plannedStartDate" className="form-label">
                      Planned Start Date
                    </label>
                    <input
                      id="plannedStartDate"
                      type="date"
                      value={formData.plannedStartDate}
                      onChange={(e) => handleInputChange('plannedStartDate', e.target.value)}
                      className="form-input"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.plannedStartDate && (
                      <p className="form-error">{errors.plannedStartDate}</p>
                    )}
                  </div>

                  {/* Planned End Date */}
                  <div className="form-group">
                    <label htmlFor="plannedEndDate" className="form-label">
                      Planned End Date
                    </label>
                    <input
                      id="plannedEndDate"
                      type="date"
                      value={formData.plannedEndDate}
                      onChange={(e) => handleInputChange('plannedEndDate', e.target.value)}
                      className="form-input"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.plannedEndDate && (
                      <p className="form-error">{errors.plannedEndDate}</p>
                    )}
                  </div>

                  {/* Project Manager */}
                  <div className="form-group">
                    <label htmlFor="projectManager" className="form-label">
                      Project Manager
                    </label>
                    <select
                      id="projectManager"
                      value={formData.projectManager}
                      onChange={(e) => handleInputChange('projectManager', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Project Manager</option>
                      {masterData.employees && masterData.employees.length > 0 ? (
                        masterData.employees.map((employee) => (
                          <option key={employee._id} value={employee._id}>
                            {employee.employeeName} {employee.designation ? `(${employee.designation})` : ''}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>No project managers available</option>
                      )}
                    </select>
                    {errors.projectManager && (
                      <p className="form-error">{errors.projectManager}</p>
                    )}
                  </div>
                </div>
              </section>
            )}

            {activeTab === 'milestones' && (
              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Milestones</h3>
                    <p>The points at which this project is delivered or billed.</p>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Project Milestones</label>
                  
                  {/* Compact Existing Milestones */}
                  {formData.milestones.length > 0 && (
                    <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {formData.milestones.map((milestone, index) => (
                        <div key={index} style={{
                          padding: '10px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '4px',
                          background: '#f9fafb'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: '8px',
                            marginBottom: isReadOnly ? '8px' : '12px'
                          }}>
                            {isReadOnly ? (
                              <h5 style={{
                                margin: 0,
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#1f2937'
                              }}>
                                {milestone.milestoneName}
                              </h5>
                            ) : (
                              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                                <label className="form-label required">Milestone Name</label>
                                <input
                                  type="text"
                                  value={milestone.milestoneName}
                                  onChange={(e) => updateMilestone(index, 'milestoneName', e.target.value)}
                                  className="form-input"
                                  placeholder="Enter milestone name"
                                  maxLength={100}
                                />
                              </div>
                            )}
                            {!isReadOnly && (
                              <button
                                type="button"
                                onClick={() => removeMilestone(index)}
                                style={{
                                  padding: '4px 6px',
                                  marginTop: '22px',
                                  color: '#dc2626',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  borderRadius: '3px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                title="Remove milestone"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                          {isReadOnly ? (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: milestone.milestoneDescription ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)',
                            gap: '8px',
                            fontSize: '12px'
                          }}>
                            <div style={{
                              background: 'white',
                              padding: '6px 8px',
                              borderRadius: '3px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <span style={{
                                fontWeight: 500,
                                color: 'var(--color-text-secondary, #5B6478)',
                                display: 'block',
                                marginBottom: '2px',
                                fontSize: '11px'
                              }}>Planned Date:</span>
                              <div style={{ color: '#1f2937', fontSize: '12px' }}>
                                {milestone.plannedDate ? new Date(milestone.plannedDate).toLocaleDateString() : '-'}
                              </div>
                            </div>
                            <div style={{
                              background: 'white',
                              padding: '6px 8px',
                              borderRadius: '3px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <span style={{
                                fontWeight: 500,
                                color: 'var(--color-text-secondary, #5B6478)',
                                display: 'block',
                                marginBottom: '2px',
                                fontSize: '11px'
                              }}>Billing Amount:</span>
                              <div style={{ color: '#1f2937', fontSize: '12px' }}>
                                {formData.currency} {(milestone.billingAmount || 0).toLocaleString()}
                              </div>
                            </div>
                            <div style={{
                              background: 'white',
                              padding: '6px 8px',
                              borderRadius: '3px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <span style={{
                                fontWeight: 500,
                                color: 'var(--color-text-secondary, #5B6478)',
                                display: 'block',
                                marginBottom: '2px',
                                fontSize: '11px'
                              }}>Status:</span>
                              <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '2px 6px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 500,
                                background: milestone.isBillable ? '#d1fae5' : '#f3f4f6',
                                color: milestone.isBillable ? '#065f46' : '#374151'
                              }}>
                                {milestone.isBillable ? 'Billable' : 'Non-billable'}
                              </div>
                            </div>
                            {milestone.milestoneDescription && (
                              <div style={{
                                background: 'white',
                                padding: '6px 8px',
                                borderRadius: '3px',
                                border: '1px solid #e5e7eb'
                              }}>
                                <span style={{
                                  fontWeight: 500,
                                  color: 'var(--color-text-secondary, #5B6478)',
                                  display: 'block',
                                  marginBottom: '2px',
                                  fontSize: '11px'
                                }}>Description:</span>
                                <div style={{
                                  color: '#1f2937',
                                  fontSize: '12px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {milestone.milestoneDescription}
                                </div>
                              </div>
                            )}
                          </div>
                          ) : (
                          <div className="pay-grid pay-grid-3">
                            <div className="form-group">
                              <label className="form-label required">Planned Date</label>
                              <input
                                type="date"
                                value={milestone.plannedDate || ''}
                                onChange={(e) => updateMilestone(index, 'plannedDate', e.target.value)}
                                className="form-input"
                                min={formData.plannedStartDate || ''}
                                max={formData.plannedEndDate || ''}
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Billing Amount</label>
                              <div className="pay-money">
                                <span className="pay-money-code">{formData.currency || 'INR'}</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={milestone.billingAmount}
                                  onChange={(e) => updateMilestone(index, 'billingAmount', parseFloat(e.target.value) || 0)}
                                  className="form-input"
                                />
                              </div>
                            </div>
                            <div className="form-group">
                              <label className="form-label">Is Billable</label>
                              <select
                                value={milestone.isBillable ? 'yes' : 'no'}
                                onChange={(e) => updateMilestone(index, 'isBillable', e.target.value === 'yes')}
                                className="form-input"
                              >
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label className="form-label">Milestone Description</label>
                              <input
                                type="text"
                                value={milestone.milestoneDescription || ''}
                                onChange={(e) => updateMilestone(index, 'milestoneDescription', e.target.value)}
                                className="form-input"
                                placeholder="Enter milestone description (optional)"
                                maxLength={200}
                              />
                            </div>
                          </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Compact Add New Milestone Form */}
                  {!isReadOnly && (
                    <div style={{ 
                      padding: '12px 16px', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '4px', 
                      background: '#f9fafb',
                      marginTop: '12px'
                    }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '12px',
                      gap: '6px'
                    }}>
                      <Plus size={16} style={{ color: '#222448' }} />
                      <h4 style={{ 
                        margin: 0, 
                        fontSize: '14px', 
                        fontWeight: 600, 
                        color: '#1f2937' 
                      }}>Add New Milestone</h4>
                    </div>
                    
                    <div className="pay-grid pay-grid-3">
                      <div className="form-group">
                        <label htmlFor="milestoneName" className="form-label required">
                          Milestone Name
                        </label>
                        <input
                          id="milestoneName"
                          type="text"
                          value={newMilestone.milestoneName}
                          onChange={(e) => setNewMilestone(prev => ({ ...prev, milestoneName: e.target.value }))}
                          onKeyPress={(e) => e.key === 'Enter' && addMilestone()}
                          className="form-input"
                          placeholder="Enter milestone name"
                          maxLength={100}
                          autoFocus
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="milestoneDate" className="form-label required">
                          Planned Date
                        </label>
                        <input
                          id="milestoneDate"
                          type="date"
                          value={newMilestone.plannedDate}
                          onChange={(e) => setNewMilestone(prev => ({ ...prev, plannedDate: e.target.value }))}
                          className="form-input"
                          min={formData.plannedStartDate || new Date().toISOString().split('T')[0]}
                          max={formData.plannedEndDate || ''}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="milestoneAmount" className="form-label">
                          Billing Amount
                        </label>
                        <div className="pay-money">
                          <span className="pay-money-code">{formData.currency || 'INR'}</span>
                          <input
                            id="milestoneAmount"
                            type="number"
                            min="0"
                            step="0.01"
                            value={newMilestone.billingAmount}
                            onChange={(e) => setNewMilestone(prev => ({ ...prev, billingAmount: parseFloat(e.target.value) || 0 }))}
                            className="form-input"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="milestoneDescription" className="form-label">
                          Milestone Description
                        </label>
                        <input
                          id="milestoneDescription"
                          type="text"
                          value={newMilestone.milestoneDescription}
                          onChange={(e) => setNewMilestone(prev => ({ ...prev, milestoneDescription: e.target.value }))}
                          className="form-input"
                          placeholder="Enter milestone description (optional)"
                          maxLength={200}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="milestoneBillable" className="form-label">
                          Is Billable
                        </label>
                        <select
                          id="milestoneBillable"
                          value={newMilestone.isBillable ? 'yes' : 'no'}
                          onChange={(e) => setNewMilestone(prev => ({ ...prev, isBillable: e.target.value === 'yes' }))}
                          className="form-input"
                        >
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Action Buttons Row */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setNewMilestone({
                            milestoneName: '',
                            milestoneDescription: '',
                            plannedDate: '',
                            deliverables: [],
                            acceptanceCriteria: [],
                            isBillable: true,
                            billingAmount: 0
                          })
                        }}
                        className="btn-secondary"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={addMilestone}
                        disabled={!newMilestone.milestoneName.trim() || !newMilestone.plannedDate}
                        className="btn-primary"
                      >
                        Add Milestone
                      </button>
                    </div>
                    
                    {/* Compact Validation Message */}
                    {(!newMilestone.milestoneName.trim() || !newMilestone.plannedDate) && (
                      <div style={{ 
                        marginTop: '8px', 
                        padding: '8px 12px', 
                        background: '#fef3c7', 
                        border: '1px solid #fbbf24', 
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#92400e',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <AlertCircle size={14} />
                        <span>Please fill in the milestone name and planned date to add a milestone.</span>
                      </div>
                    )}
                  </div>
                  )}
                  
                  {/* Compact Milestone Summary */}
                  {formData.milestones.length > 0 && (
                    <div style={{
                      marginTop: '12px',
                      padding: '10px 12px',
                      background: '#f3f4f6',
                      borderRadius: '4px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <h5 style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: '13px', 
                        fontWeight: 600, 
                        color: '#1f2937' 
                      }}>
                        Milestone Summary
                      </h5>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(3, 1fr)', 
                        gap: '12px',
                        fontSize: '12px'
                      }}>
                        <div>
                          <span style={{ color: 'var(--color-text-secondary, #5B6478)' }}>Total Milestones:</span>
                          <span style={{ marginLeft: '6px', fontWeight: 600, color: '#1f2937' }}>
                            {formData.milestones.length}
                          </span>
                        </div>
                        <div>
                          <span style={{ color: 'var(--color-text-secondary, #5B6478)' }}>Total Billable Amount:</span>
                          <span style={{ marginLeft: '6px', fontWeight: 600, color: '#1f2937' }}>
                            {formData.currency} {formData.milestones
                              .filter(m => m.isBillable)
                              .reduce((sum, m) => sum + (m.billingAmount || 0), 0)
                              .toLocaleString()}
                          </span>
                        </div>
                        <div>
                          <span style={{ color: 'var(--color-text-secondary, #5B6478)' }}>Billable Milestones:</span>
                          <span style={{ marginLeft: '6px', fontWeight: 600, color: '#1f2937' }}>
                            {formData.milestones.filter(m => m.isBillable).length}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}

            {activeTab === 'documents' && (
              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Documents</h3>
                    <p>The statement of work and any other file that belongs with this project.</p>
                  </div>
                </div>
                <div className="pay-grid pay-grid-2">
                  <DocumentDropzone
                    label="Statement of Work (SOW)"
                    hint="Drop the statement of work here"
                    file={formData.documents.sow}
                    disabled={isReadOnly}
                    onFile={(file) => handleFileUpload(file, 'sow')}
                  />
                  <DocumentDropzone
                    label="Non-Disclosure Agreement (NDA)"
                    hint="Drop the agreement here"
                    file={formData.documents.nda}
                    disabled={isReadOnly}
                    onFile={(file) => handleFileUpload(file, 'nda')}
                  />
                </div>
              </section>
            )}

            {activeTab === 'settings' && (
              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Settings</h3>
                    <p>Time tracking and the other options for this project.</p>
                  </div>
                </div>
                <div className="pay-grid pay-grid-2">
                  {/* Time Tracking Settings Card */}
                  <div style={{
                    padding: '16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    background: '#ffffff'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: '1px solid #f3f4f6'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '4px',
                        background: '#eff6ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#3b82f6'
                      }}>
                        <Clock size={18} />
                      </div>
                      <h4 style={{
                        margin: 0,
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#1f2937'
                      }}>Time & Expense Tracking</h4>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '10px',
                        borderRadius: '4px',
                        transition: 'background 0.2s',
                        background: formData.settings.allowTimeTracking ? '#F0EBF7' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = formData.settings.allowTimeTracking ? '#F0EBF7' : 'transparent'}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                          <CheckCircle2 
                            size={16} 
                            style={{ 
                              color: formData.settings.allowTimeTracking ? '#10b981' : '#d1d5db',
                              flexShrink: 0
                            }} 
                          />
                          <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
                            Allow time tracking
                          </span>
                        </div>
                        <div style={{
                          width: '40px',
                          height: '20px',
                          borderRadius: '10px',
                          background: formData.settings.allowTimeTracking ? '#10b981' : '#d1d5db',
                          position: 'relative',
                          cursor: isReadOnly ? 'not-allowed' : 'pointer',
                          transition: 'background 0.2s',
                          flexShrink: 0,
                          opacity: isReadOnly ? 0.6 : 1
                        }}
                        onClick={!isReadOnly ? () => handleNestedInputChange('settings', 'allowTimeTracking', !formData.settings.allowTimeTracking) : undefined}>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: 'white',
                            position: 'absolute',
                            top: '2px',
                            left: formData.settings.allowTimeTracking ? '22px' : '2px',
                            transition: 'left 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }} />
                        </div>
                      </label>
                      
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '10px',
                        borderRadius: '4px',
                        transition: 'background 0.2s',
                        background: formData.settings.allowExpenseTracking ? '#F0EBF7' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = formData.settings.allowExpenseTracking ? '#F0EBF7' : 'transparent'}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                          <CheckCircle2 
                            size={16} 
                            style={{ 
                              color: formData.settings.allowExpenseTracking ? '#10b981' : '#d1d5db',
                              flexShrink: 0
                            }} 
                          />
                          <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
                            Allow expense tracking
                          </span>
                        </div>
                        <div style={{
                          width: '40px',
                          height: '20px',
                          borderRadius: '10px',
                          background: formData.settings.allowExpenseTracking ? '#10b981' : '#d1d5db',
                          position: 'relative',
                          cursor: isReadOnly ? 'not-allowed' : 'pointer',
                          transition: 'background 0.2s',
                          flexShrink: 0,
                          opacity: isReadOnly ? 0.6 : 1
                        }}
                        onClick={!isReadOnly ? () => handleNestedInputChange('settings', 'allowExpenseTracking', !formData.settings.allowExpenseTracking) : undefined}>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: 'white',
                            position: 'absolute',
                            top: '2px',
                            left: formData.settings.allowExpenseTracking ? '22px' : '2px',
                            transition: 'left 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }} />
                        </div>
                      </label>
                      
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '10px',
                        borderRadius: '4px',
                        transition: 'background 0.2s',
                        background: formData.settings.requireApprovalForExpenses ? '#fef3c7' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = formData.settings.requireApprovalForExpenses ? '#fef3c7' : 'transparent'}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                          <Receipt 
                            size={16} 
                            style={{ 
                              color: formData.settings.requireApprovalForExpenses ? '#f59e0b' : '#d1d5db',
                              flexShrink: 0
                            }} 
                          />
                          <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
                            Require approval for expenses
                          </span>
                        </div>
                        <div style={{
                          width: '40px',
                          height: '20px',
                          borderRadius: '10px',
                          background: formData.settings.requireApprovalForExpenses ? '#f59e0b' : '#d1d5db',
                          position: 'relative',
                          cursor: isReadOnly ? 'not-allowed' : 'pointer',
                          transition: 'background 0.2s',
                          flexShrink: 0,
                          opacity: isReadOnly ? 0.6 : 1
                        }}
                        onClick={!isReadOnly ? () => handleNestedInputChange('settings', 'requireApprovalForExpenses', !formData.settings.requireApprovalForExpenses) : undefined}>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: 'white',
                            position: 'absolute',
                            top: '2px',
                            left: formData.settings.requireApprovalForExpenses ? '22px' : '2px',
                            transition: 'left 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }} />
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Notification Settings Card */}
                  <div style={{
                    padding: '16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    background: '#ffffff'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: '1px solid #f3f4f6'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '4px',
                        background: '#fef3c7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#f59e0b'
                      }}>
                        <Bell size={18} />
                      </div>
                      <h4 style={{
                        margin: 0,
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#1f2937'
                      }}>Notifications & Automation</h4>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '10px',
                        borderRadius: '4px',
                        transition: 'background 0.2s',
                        background: formData.settings.notifyOnMilestoneCompletion ? '#F0EBF7' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = formData.settings.notifyOnMilestoneCompletion ? '#F0EBF7' : 'transparent'}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                          <CheckCircle2 
                            size={16} 
                            style={{ 
                              color: formData.settings.notifyOnMilestoneCompletion ? '#3b82f6' : '#d1d5db',
                              flexShrink: 0
                            }} 
                          />
                          <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
                            Notify on milestone completion
                          </span>
                        </div>
                        <div style={{
                          width: '40px',
                          height: '20px',
                          borderRadius: '10px',
                          background: formData.settings.notifyOnMilestoneCompletion ? '#3b82f6' : '#d1d5db',
                          position: 'relative',
                          cursor: isReadOnly ? 'not-allowed' : 'pointer',
                          transition: 'background 0.2s',
                          flexShrink: 0,
                          opacity: isReadOnly ? 0.6 : 1
                        }}
                        onClick={!isReadOnly ? () => handleNestedInputChange('settings', 'notifyOnMilestoneCompletion', !formData.settings.notifyOnMilestoneCompletion) : undefined}>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: 'white',
                            position: 'absolute',
                            top: '2px',
                            left: formData.settings.notifyOnMilestoneCompletion ? '22px' : '2px',
                            transition: 'left 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }} />
                        </div>
                      </label>
                      
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '10px',
                        borderRadius: '4px',
                        transition: 'background 0.2s',
                        background: formData.settings.notifyOnBudgetExceeded ? '#fee2e2' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = formData.settings.notifyOnBudgetExceeded ? '#fee2e2' : 'transparent'}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                          <AlertCircle 
                            size={16} 
                            style={{ 
                              color: formData.settings.notifyOnBudgetExceeded ? '#dc2626' : '#d1d5db',
                              flexShrink: 0
                            }} 
                          />
                          <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
                            Notify on budget exceeded
                          </span>
                        </div>
                        <div style={{
                          width: '40px',
                          height: '20px',
                          borderRadius: '10px',
                          background: formData.settings.notifyOnBudgetExceeded ? '#dc2626' : '#d1d5db',
                          position: 'relative',
                          cursor: isReadOnly ? 'not-allowed' : 'pointer',
                          transition: 'background 0.2s',
                          flexShrink: 0,
                          opacity: isReadOnly ? 0.6 : 1
                        }}
                        onClick={!isReadOnly ? () => handleNestedInputChange('settings', 'notifyOnBudgetExceeded', !formData.settings.notifyOnBudgetExceeded) : undefined}>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: 'white',
                            position: 'absolute',
                            top: '2px',
                            left: formData.settings.notifyOnBudgetExceeded ? '22px' : '2px',
                            transition: 'left 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }} />
                        </div>
                      </label>
                      
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '10px',
                        borderRadius: '4px',
                        transition: 'background 0.2s',
                        background: formData.settings.autoInvoiceGeneration ? '#f0fdf4' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = formData.settings.autoInvoiceGeneration ? '#f0fdf4' : 'transparent'}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                          <Receipt 
                            size={16} 
                            style={{ 
                              color: formData.settings.autoInvoiceGeneration ? '#10b981' : '#d1d5db',
                              flexShrink: 0
                            }} 
                          />
                          <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
                            Auto invoice generation
                          </span>
                        </div>
                        <div style={{
                          width: '40px',
                          height: '20px',
                          borderRadius: '10px',
                          background: formData.settings.autoInvoiceGeneration ? '#10b981' : '#d1d5db',
                          position: 'relative',
                          cursor: isReadOnly ? 'not-allowed' : 'pointer',
                          transition: 'background 0.2s',
                          flexShrink: 0,
                          opacity: isReadOnly ? 0.6 : 1
                        }}
                        onClick={!isReadOnly ? () => handleNestedInputChange('settings', 'autoInvoiceGeneration', !formData.settings.autoInvoiceGeneration) : undefined}>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: 'white',
                            position: 'absolute',
                            top: '2px',
                            left: formData.settings.autoInvoiceGeneration ? '22px' : '2px',
                            transition: 'left 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }} />
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </div>

          {/* Form Actions */}
          {!isViewMode && (
            <div className="form-actions" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                {!isEditMode && (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                )}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => navigate('/projects/list')}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                  ) : (
                    <span>{isEditMode ? 'Update Project' : 'Create Project'}</span>
                  )}
                </button>
              </div>
            </div>
          )}
          {isViewMode && (
            <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/projects/list')}
              >
                Back
              </button>
            </div>
          )}
        </form>
      </div>

      {/* Confirmation Modal for Milestone Deletion */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Remove Milestone"
        message={`Are you sure you want to remove the milestone "${deleteModal.milestoneName}"?`}
        confirmText="Remove"
        cancelText="Cancel"
        type="danger"
      />

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Project Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default ProjectForm